import { PureComponent } from "react";
import { Input as KInput } from "@progress/kendo-react-inputs";
import { v4 as uuid } from "uuid";

import { Key } from "../../constants/keyboard";
import { getPastedValue } from "../../utils/paste";
import { ErrorWrap } from "../ErrorWrap/ErrorWrap";

import "./Input.scss";

import type { ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent, RefObject } from "react";
import type {
  InputChangeEvent,
  InputHandle,
  InputProps as KInputProps,
} from "@progress/kendo-react-inputs";
import type { ValidationErrors } from "../../models";

interface InputProps extends Omit<KInputProps, "onChange" | "pattern" | "onBlur"> {
  id?: string;
  name: string;
  inputClass?: string;
  value?: string | number;
  disabled?: boolean;
  maxLength?: number;
  label: string;
  error?: ValidationErrors; // -> error
  pattern?: RegExp;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onBeforeChange?: (value: string) => { valid: boolean; value: string };
  onChange?: (fieldName: string, value: string, e: ChangeEvent<HTMLInputElement>) => void;
  onCorrectValueBeforeBlur?: (value: string) => string;
  onBlur?: (fieldName: string, value: string, e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  click?: (e: MouseEvent<HTMLElement>) => void;
  // special complex validation / correction
  onBeforePaste?: (pastedText: string) => string;
  minLength?: number;
  inputRef?: RefObject<InputHandle>;
  title?: string;
  dataTitle?: string;
  readOnly?: boolean;
  onlyComponent?: boolean; // do not wrap with ErrorWrap. We have usual Inputs on some pages
  blurOnEnter?: boolean; // send blur after Enter (it is necessary for Encounters searching)
  dataTestId: string; // for testing
}

interface InputState {
  value: string;
  propsValue?: string; // for comparison with previous props
  isFocused?: boolean;
}

export class Input extends PureComponent<InputProps, InputState> {
  static defaultProps = {
    content: "",
    editMode: false,
    error: "",
    inputClass: "",
  };

  el: HTMLInputElement;
  valueWasPasted: boolean;

  constructor(opts: InputProps) {
    super(opts);

    this.onChangeInput = this.onChangeInput.bind(this);
    this.onBlurInput = this.onBlurInput.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onWrapClick = this.onWrapClick.bind(this);
    this.onPasteInput = this.onPasteInput.bind(this);
    this.onKeyDownInput = this.onKeyDownInput.bind(this);

    this.valueWasPasted = false;
    this.state = { value: opts.value ? opts.value.toString() : "" };
  }

  onChangeInput(e: InputChangeEvent) {
    let val = e.target.value as string;

    if (this.valueWasPasted) {
      this.valueWasPasted = false;

      const value =
        this.props.onBeforePaste ?
          this.props.onBeforePaste(val)
        : getPastedValue(val, this.props.maxLength, this.props.pattern);

      this.setState({ value }, () => {
        if (this.props.onChange) {
          this.props.onChange(
            this.props.name,
            value,
            e.syntheticEvent as ChangeEvent<HTMLInputElement>,
          );
        }
      });

      return;
    }

    if (val && this.props.pattern && !val.match(this.props.pattern)) {
      return;
    }
    if (this.props.maxLength && val && val.length > this.props.maxLength) {
      return;
    }
    if (this.props.onBeforeChange) {
      const { valid, value } = this.props.onBeforeChange(val || "");
      if (!valid) {
        return;
      }
      val = value;
    }
    this.setState({ value: val }, () => {
      if (this.props.onChange) {
        this.props.onChange(
          this.props.name,
          val,
          e.syntheticEvent as ChangeEvent<HTMLInputElement>,
        );
      }
    });
  }

  onBlurInput(e: FocusEvent<HTMLInputElement>) {
    e.target.classList.remove("tc-state-focused");
    let { value } = e.target;
    if (this.props.onCorrectValueBeforeBlur) {
      value = this.props.onCorrectValueBeforeBlur(value);
    }

    // update value in state to corrected
    this.setState({
      value,
      isFocused: false,
    });

    if (this.props.onBlur) {
      this.props.onBlur(this.props.name, value, e);
    }
  }

  onFocusInput = (e: FocusEvent<HTMLInputElement>) => {
    e.target.classList.add("tc-state-focused");

    if (!this.state.isFocused) {
      this.setState({ isFocused: true });
    }

    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  };

  private onPasteInput = () => {
    this.valueWasPasted = true;
  };

  onKeyDownInput(e: KeyboardEvent<HTMLInputElement>) {
    // update value in the parent component after keyDown with Enter
    if (this.props.blurOnEnter && e.key === Key.Enter && this.props.onBlur) {
      let { value } = this.state;
      if (this.props.onCorrectValueBeforeBlur) {
        value = this.props.onCorrectValueBeforeBlur(value);
      }

      this.props.onBlur(this.props.name, value);
    }

    if (this.props.onKeyDown) {
      this.props.onKeyDown(e);
    }
  }

  onClick(e: MouseEvent<HTMLAnchorElement>) {
    if (!this.props.disabled && this.props.click) {
      this.props.click(e);
    }
  }

  onWrapClick(e: MouseEvent<HTMLSpanElement>) {
    if (!this.props.disabled) {
      e.currentTarget.focus();
    }
  }

  static getDerivedStateFromProps(nextProps: InputProps, prevState: InputState) {
    if (nextProps.value !== prevState.propsValue) {
      return {
        propsValue: nextProps.value,
        value: nextProps.value ? nextProps.value.toString() : "",
      };
    }

    return null;
  }

  render() {
    const id = this.props.id || `auto-${uuid()}`;
    const placeholder = this.props.placeholder || " ";
    const { error } = this.props;
    const disabled = typeof this.props.disabled !== "undefined" && this.props.disabled;
    const className =
      !disabled ?
        `tc-input form-input ${this.props.inputClass} k-input wrapped-input-control ${this.state.value ? " has-text" : ""}`
      : `tc-input form-input k-state-disabled ${this.props.inputClass} k-input wrapped-input-control ${this.state.value ? " has-text" : ""}`;
    const floatingLabelInput = (
      // TODO: pass in all props using spread after plucking the props we override?
      // this would allow us to stop manually maintaining parity with kendo props as they evolve
      <KInput
        autoComplete="off"
        className={className}
        data-lpignore="true"
        data-testid={this.props.dataTestId}
        data-title={this.props.dataTitle}
        disabled={disabled}
        id={id}
        label={this.props.label}
        maxLength={this.props.maxLength}
        minLength={typeof this.props.minLength === "number" ? this.props.minLength : undefined}
        onBlur={!disabled ? this.onBlurInput : undefined}
        onChange={!disabled ? this.onChangeInput : undefined}
        onFocus={!disabled ? this.onFocusInput : undefined}
        onKeyDown={!disabled ? this.onKeyDownInput : undefined}
        onPaste={!disabled ? this.onPasteInput : undefined}
        placeholder={placeholder}
        readOnly={this.props.readOnly || disabled ? true : undefined}
        ref={this.props.inputRef}
        required={this.props.required}
        title={this.props.title}
        validationMessage={this.props.validationMessage}
        value={this.state.value}
      />
    );

    if (this.props.onlyComponent) {
      return floatingLabelInput;
    }

    return (
      <ErrorWrap id={`error-${id}`} error={error}>
        {floatingLabelInput}
      </ErrorWrap>
    );
  }
}
